import type { ProductHit } from '@libs/algolia';
import type {
    CustomAddress,
    DeliveryMethodType,
    ProductApiResponse,
    RelatedProductsApiResponse,
} from '@libs/common-types';
import { evaluateSession, HttpMethod, logger } from '@libs/core';

import { cweaClient } from '../clients/api-proxy-browser-client';
import { CWEA_ENDPOINTS } from '../constants';

type ProductApiNotFoundResponse = {
    message: string;
};

const isProductApiResponse = (data: unknown): data is ProductApiResponse => {
    if (!data || typeof data !== 'object') return false;

    const response = data as Record<string, unknown>;
    if (!('data' in response) || !response.data || typeof response.data !== 'object') {
        return false;
    }

    return 'variants' in response.data;
};

export async function getProductBase(id: string, onlineAt?: number) {
    const url = CWEA_ENDPOINTS.product.getProductBase(id, onlineAt);
    logger.info(`[GET PRODUCT BASE]: ${url}`);

    const data = await cweaClient.get<ProductApiResponse | ProductApiNotFoundResponse>(url, {
        useOldFetch: true,
        additionalSuccessCodes: [404],
    });
    if (!data) {
        const error = new Error(`[GET PRODUCT BASE][ERROR] ${url}: Something went wrong`) as Error & {
            statusCode: number;
        };
        error.statusCode = 500;
        throw error;
    }

    // 404 is treated as a "success" so fetch returns data instead of null,
    // allowing us to log the error in Vercel. We therefore need to manually
    // validate the response and treat invalid data as a 404.
    if (!isProductApiResponse(data)) {
        const error = new Error(`[GET PRODUCT BASE][ERROR] ${url}: ${data.message || 'Invalid Response'}`) as Error & {
            statusCode: number;
        };
        error.statusCode = 404;
        throw error;
    }

    return data;
}

type GetProductTailored = {
    deliveryMethodsToRetrieve: DeliveryMethodType[];
    storeId?: string;
    customAddress?: CustomAddress;
    variantSkus?: string[];
};

export async function getProductTailored<T>({
    storeId,
    customAddress,
    deliveryMethodsToRetrieve,
    variantSkus = [],
}: GetProductTailored): Promise<T> {
    if (typeof window !== 'undefined') {
        const sessionStillValid = await evaluateSession({ httpMethod: HttpMethod.GET });
        if (!sessionStillValid) {
            throw new Error('[GET PRODUCT TAILORED] Session ended');
        }
    }

    const url = CWEA_ENDPOINTS.product.getProductTailored;
    logger.info(`[GET PRODUCT TAILORED]: ${url}`);

    const body = {
        storeId,
        customAddress,
        deliveryMethodsToRetrieve,
        variantSkus,
    };

    const result: Nullable<T> = await cweaClient.post(url, {
        body,
        useOldFetch: true,
    });

    if (!result) {
        logger.error(`[GET PRODUCT TAILORED][Error]: ${url}`);
        throw new Error(`[GET PRODUCT TAILORED]: ${url} returned null`);
    }

    return result;
}

export async function getRelatedProducts(sku: string): Promise<RelatedProductsApiResponse> {
    const url = CWEA_ENDPOINTS.product.getRelatedProducts(sku);
    logger.info(`[GET RELATED PRODUCTS]: ${url}`);

    const result = await cweaClient.get<RelatedProductsApiResponse>(url, {
        useOldFetch: true,
    });

    if (!result) {
        logger.error(`[GET RELATED PRODUCTS][Error]: ${url}`);
        throw new Error(`[GET RELATED PRODUCTS]: ${url} returned null`);
    }

    return result;
}

type GetAlgoliaProductById = {
    id: string;
    locale: string;
    caller?: string;
};

type AlgoliaProductErrorResponse = {
    error: string;
    message: string;
};

const createApiProxyError = (message: string) => {
    const error = new Error(message) as Error & { status: number };
    error.status = 500;
    return error;
};

/**
 * Fetches a single product by ID
 * @param id - Product SKU or Web Style ID
 * @param locale - Locale for the Algolia index
 */
export async function getAlgoliaProductById({ id, locale, caller }: GetAlgoliaProductById): Promise<ProductHit | null> {
    const url = CWEA_ENDPOINTS.product.getAlgoliaProductById(locale, id);
    logger.info(`[GET ALGOLIA PRODUCT BY ID]: ${url}`);

    try {
        const data = await cweaClient.get<ProductHit | AlgoliaProductErrorResponse>(url, {
            useOldFetch: true,
            additionalSuccessCodes: [404],
            ...(caller ? { headers: { 'x-petm-algolia-caller': caller } } : {}),
        });

        if (!data) {
            logger.warn(`[GET ALGOLIA PRODUCT BY ID][ERROR] ${url}: No data returned`);
            return null;
        }

        // Check if it's an error response
        if ('error' in data) {
            logger.warn(`[GET ALGOLIA PRODUCT BY ID][ERROR] ${url}: ${data.message || data.error}`);
            return null;
        }

        return data;
    } catch (err) {
        logger.error(`[GET ALGOLIA PRODUCT BY ID][ERROR] ${url}:`, err);
        return null;
    }
}

type GetAlgoliaProductsByIds = {
    ids: string[];
    locale: string;
    caller?: string;
    throwOnError?: boolean;
};

/**
 * Fetches multiple products by IDs
 * @param ids - Array of product SKUs or Web Style IDs
 * @param locale - Locale for the Algolia index
 */
export async function getAlgoliaProductsByIds({
    ids,
    locale,
    caller,
    throwOnError,
}: GetAlgoliaProductsByIds): Promise<ProductHit[]> {
    const url = CWEA_ENDPOINTS.product.getAlgoliaProductsByIds(locale);
    logger.info(`[GET ALGOLIA PRODUCTS BY IDS]: ${url}`);

    try {
        const body = { ids };

        const data = await cweaClient.post<ProductHit[] | AlgoliaProductErrorResponse, typeof body>(url, {
            body,
            useOldFetch: true,
            ...(caller ? { headers: { 'x-petm-algolia-caller': caller } } : {}),
        });

        if (!data) {
            logger.warn(`[GET ALGOLIA PRODUCTS BY IDS][ERROR] ${url}: No data returned`);
            if (throwOnError) {
                throw createApiProxyError(`[GET ALGOLIA PRODUCTS BY IDS][ERROR] ${url}: No data returned`);
            }
            return [];
        }

        // Check if it's an error response
        if ('error' in data) {
            logger.warn(`[GET ALGOLIA PRODUCTS BY IDS][ERROR] ${url}: ${data.message || data.error}`);
            if (throwOnError) {
                throw createApiProxyError(
                    `[GET ALGOLIA PRODUCTS BY IDS][ERROR] ${url}: ${data.message || data.error || 'Invalid Response'}`
                );
            }
            return [];
        }

        return data;
    } catch (err) {
        logger.error(`[GET ALGOLIA PRODUCTS BY IDS][ERROR] ${url}:`, err);
        if (throwOnError) {
            throw err;
        }
        return [];
    }
}
